/**
 * Raid-target danger legibility (any raidable NPC island, not just lairs: the
 * periphery merchant outposts now sit at d1-2 too). Maps a danger rating to a
 * 5-step severity scale, calibrated on the backend `app:combat:simulate`
 * win-rates:
 *
 *   emerald  d1-2   "Abordable"  — the entry rung, but a mirror match: 2 bricks clear
 *                                  d1 (98%), a lone brick is a coin-flip (55%); d2 wants
 *                                  3 bricks (95%) or a frigate (80%), 2 bricks is 52%
 *   lime     d3-4   "Modéré"     — first real wall: a lone frigate is wiped at d3
 *                                  (6% win); needs frégate + 2 bricks (81%) or 2 frégates
 *   amber    d5-6   "Redoutable" — combined fleet: 2 frégates (81% @d5) or a galion (70%)
 *   orange   d7-8   "Périlleux"  — heavy only: at d8 even galion+frégate is a coin-flip (50%)
 *   red      d9-10  "Mortel"     — apex: only 2 galions + 2 frégates clears d10 (98%)
 *
 * Drives the map marker badge, the side-panel danger readout AND the raid panel,
 * so difficulty reads consistently everywhere and two adjacent dangers (a d3
 * comptoir vs a d4 island) land in the same tier instead of looking wildly apart.
 * The live per-fleet win-rate estimate (useRaidEstimate) remains authoritative;
 * `advice` is only a starting steer.
 */
export interface DangerTier {
  label: string
  /** Side-panel number colour class. */
  text: string
  /** Map marker badge classes (bg + text + border colour). */
  badge: string
  /** What fleet to bring — shown next to a raidable lair. */
  advice: string
}

export function dangerTier(danger: number): DangerTier {
  if (danger >= 9) {
    return {
      label: 'Mortel',
      text: 'text-red-400 font-bold',
      badge: 'bg-ocean-900 text-red-400 border-red-500',
      advice: 'Flotte lourde indispensable : 2 galions épaulés de frégates.',
    }
  }
  if (danger >= 7) {
    return {
      label: 'Périlleux',
      text: 'text-orange-400 font-semibold',
      badge: 'bg-ocean-900 text-orange-400 border-orange-500',
      advice: 'Du lourd : galion + frégates. Un galion seul est trop juste.',
    }
  }
  if (danger >= 5) {
    return {
      label: 'Redoutable',
      text: 'text-amber-300 font-semibold',
      badge: 'bg-ocean-900 text-amber-300 border-amber-400',
      advice: '2 frégates, ou un galion.',
    }
  }
  if (danger >= 3) {
    return {
      label: 'Modéré',
      text: 'text-lime-300 font-semibold',
      badge: 'bg-ocean-900 text-lime-300 border-lime-400',
      advice: 'Une frégate + 2 bricks, ou 2 frégates.',
    }
  }
  // Same label, same colour, different steer. d1 and d2 are the ENTRY rungs of both
  // ladders (a danger-1 lair and a danger-1 outpost field the identical roster), and
  // they are MIRROR matches: the roster is 1 brick at d1 and 2 at d2, with no fort.
  // Bringing exactly what it has is therefore a coin-flip, and a sunk hull is gone
  // for good. Measured on bare hulls (a fresh account owns no module), 4000 battles:
  //   1 brick vs d1 = 55% (45% total wipe) | 2 bricks vs d1 = 98%
  //   2 bricks vs d2 = 52% (48% wipe) | 3 bricks vs d2 = 95% | 1 frigate vs d2 = 80%
  // The old blanket "une frégate, ou 3 bricks" was safe but named a fleet a day-one
  // captain can neither build (frigate needs Chantier 2) nor sail (3 hulls exceed
  // MISSION_CAP_PER_SHIPYARD_LEVEL at Chantier 1). d1 is the rung that fits both.
  if (danger >= 2) {
    return {
      label: 'Abordable',
      text: 'text-emerald-300 font-semibold',
      badge: 'bg-ocean-900 text-emerald-300 border-emerald-400',
      advice: 'Trois bricks, ou une frégate. À deux bricks, c\'est un pile ou face.',
    }
  }
  return {
    label: 'Abordable',
    text: 'text-emerald-300 font-semibold',
    badge: 'bg-ocean-900 text-emerald-300 border-emerald-400',
    advice: 'Deux bricks. Ton brick de départ seul ne l\'emporte qu\'une fois sur deux.',
  }
}

/**
 * Defensive severity for a raid SUFFERED (the player is the defender). Separate
 * from `dangerTier` on purpose:
 *  - it's NEUTRAL — no "bring 2 galions" advice (you don't pick the fleet here);
 *  - it's UNBOUNDED — reputation-driven NPC pressure now uncaps raid danger
 *    (~12, was hard-capped at 5), so the top tier must absorb 9..∞ without
 *    saturating. The badge shows the raw number, this only colours the threat.
 *
 * Maritime escalation: Escarmouche → Raid → Flotte → Armada.
 */
export interface DefensiveTier {
  label: string
  /** Tailwind text-colour class for the danger number/label. */
  tone: string
}

export function defensiveTier(danger: number): DefensiveTier {
  if (danger >= 9) {
    return { label: 'Armada', tone: 'text-red-400' }
  }
  if (danger >= 6) {
    return { label: 'Flotte', tone: 'text-orange-400' }
  }
  if (danger >= 3) {
    return { label: 'Raid', tone: 'text-amber-300' }
  }
  return { label: 'Escarmouche', tone: 'text-emerald-300' }
}
